import { defineStore } from 'pinia';
import {
    IntegracaoAxionService,
    LicenciadoAxion,
    LicenciadoResponsavelAxion,
    UsuarioAxion,
    NegociacaoEtapaAxion,
    NegociacaoFunilAxion,
    OportunidadeAxion,
    CanalChamadoAxion,
    ServicoChamadoAxion,
    AbrirChamadoAxion,
    ChamadoAxion,
    ChamadoContatoAxion,
    OpcoesProcessoAxion,
    DispararProcessoAxion,
    ProcessoDisparadoAxion,
    ProcessoContatoAxion,
} from '@/services/integracao-axion/integracao-axion.service';
import { toast } from 'vue3-toastify';
import { useAuthStoreUser } from './authUser';
import { IdContato } from '@/services/common-types';

export const useIntegracaoAxionStore = defineStore('integracaoAxion', {
    state: () => ({
        service: new IntegracaoAxionService(),
        licenciados: [] as LicenciadoAxion[],
        funis: [] as NegociacaoFunilAxion[],
        etapas: [] as NegociacaoEtapaAxion[],
        responsaveis: [] as LicenciadoResponsavelAxion[],
        usuariosAxion: [] as UsuarioAxion[],
        oportunidades: [] as OportunidadeAxion[],
        canaisChamado: [] as CanalChamadoAxion[],
        servicosChamado: [] as ServicoChamadoAxion[],
        chamadosDoContato: [] as ChamadoContatoAxion[],
        temModuloChamadoByContato: {} as Record<string, boolean>,
        opcoesProcesso: null as OpcoesProcessoAxion | null,
        processosDoContato: [] as ProcessoContatoAxion[],
        // Descartam respostas atrasadas quando o modal é reaberto para outro contato.
        pedidoOpcoesProcesso: 0 as number,
        pedidoProcessosDoContato: 0 as number,
        isFetching: false as boolean,
        isFetchingResponsaveis: false as boolean,
        isFetchingUsuariosAxion: false as boolean,
        isFetchingOportunidades: false as boolean,
        isFetchingCanaisChamado: false as boolean,
        isFetchingServicosChamado: false as boolean,
        isFetchingChamadosDoContato: false as boolean,
        isFetchingOpcoesProcesso: false as boolean,
        isFetchingProcessosDoContato: false as boolean,
    }),

    getters: {
        etapasByFunil: (state) => (idFunil: string) =>
            state.etapas.filter(e => e.idFunil === idFunil).sort((a, b) => a.ordem - b.ordem),
    },

    actions: {
        async init() {
            const authStore = useAuthStoreUser();
            if (!authStore.currentConta?.id) return;

            if (this.isFetching) return;
            this.isFetching = true;
            try {
                const idConta = authStore.currentConta.id;
                [this.licenciados, this.funis, this.etapas] = await Promise.all([
                    this.service.listarLicenciados(idConta),
                    this.service.listarFunis(idConta),
                    this.service.listarEtapas(idConta),
                ]);
            } catch (e) {
                console.error('[integracaoAxionStore] init:', e);
                toast.error('Não foi possível carregar os dados AXION.');
            } finally {
                this.isFetching = false;
            }
        },

        async fetchResponsaveis(idLicenciado: string) {
            const authStore = useAuthStoreUser();
            if (!authStore.currentConta?.id) return;

            this.isFetchingResponsaveis = true;
            try {
                this.responsaveis = await this.service.listarResponsaveis(authStore.currentConta.id, idLicenciado);
            } catch (e) {
                console.error('[integracaoAxionStore] fetchResponsaveis:', e);
                toast.error('Não foi possível carregar os responsáveis.');
            } finally {
                this.isFetchingResponsaveis = false;
            }
        },

        async fetchOportunidades(idContato: string) {
            const authStore = useAuthStoreUser();
            if (!authStore.currentConta?.id) return;

            this.isFetchingOportunidades = true;
            try {
                this.oportunidades = await this.service.listarOportunidades(authStore.currentConta.id, idContato);
            } catch (e) {
                console.error('[integracaoAxionStore] fetchOportunidades:', e);
                toast.error('Não foi possível carregar as oportunidades.');
            } finally {
                this.isFetchingOportunidades = false;
            }
        },

        clearResponsaveis() {
            this.responsaveis = [];
        },

        async fetchUsuariosAxion() {
            const authStore = useAuthStoreUser();
            if (!authStore.currentConta?.id) return;

            this.isFetchingUsuariosAxion = true;
            try {
                this.usuariosAxion = await this.service.listarUsuariosAxion(authStore.currentConta.id);
            } catch (e) {
                console.error('[integracaoAxionStore] fetchUsuariosAxion:', e);
                toast.error('Não foi possível carregar os usuários AXION.');
            } finally {
                this.isFetchingUsuariosAxion = false;
            }
        },

        clearUsuariosAxion() {
            this.usuariosAxion = [];
        },

        clearOportunidades() {
            this.oportunidades = [];
        },

        async vincularResponsavel(idContato: IdContato, idLicenciado: string, nomeLicenciado: string, idResponsavel?: string) {
            const result = await this.service.vincularResponsavel(idContato, idLicenciado, nomeLicenciado, idResponsavel);
            if (result) {
                const chatStore = (await import('./chatStore')).useChatStore();
                const contato = chatStore.contacts.find(c => c.id === idContato);
                if (contato) {
                    contato.idClienteAxion = result.idClienteAxion;
                    contato.nomeClienteAxion = nomeLicenciado;
                    contato.idResponsavelAxion = result.idResponsavelAxion;
                }
                for (const chat of chatStore.chatList.filter(c => c.idContato === idContato)) {
                    chat.idClienteAxion = result.idClienteAxion;
                    chat.nomeClienteAxion = nomeLicenciado;
                    chat.idResponsavelAxion = result.idResponsavelAxion;
                }
                for (const chat of chatStore.chatList.filter(c => c.idContato === idContato)) {
                    chat.idClienteAxion = result.idClienteAxion;
                    chat.idResponsavelAxion = result.idResponsavelAxion;
                }
            }
            return result;
        },

        async gerarOportunidade(idContato: IdContato, nome: string, etapa: string, idResponsavel?: string) {
            return this.service.gerarOportunidade({ idContato, nome, etapa, idResponsavel });
        },

        async fetchCanaisChamado() {
            const authStore = useAuthStoreUser();
            if (!authStore.currentConta?.id) return;

            this.isFetchingCanaisChamado = true;
            try {
                this.canaisChamado = await this.service.listarCanaisChamado(authStore.currentConta.id);
            } catch (e) {
                console.error('[integracaoAxionStore] fetchCanaisChamado:', e);
                toast.error('Não foi possível carregar os canais de chamado.');
            } finally {
                this.isFetchingCanaisChamado = false;
            }
        },

        async fetchServicosChamado(idCanal?: string) {
            const authStore = useAuthStoreUser();
            if (!authStore.currentConta?.id) return;

            this.isFetchingServicosChamado = true;
            try {
                this.servicosChamado = await this.service.listarServicosChamado(authStore.currentConta.id, idCanal);
            } catch (e) {
                console.error('[integracaoAxionStore] fetchServicosChamado:', e);
                toast.error('Não foi possível carregar os serviços de chamado.');
            } finally {
                this.isFetchingServicosChamado = false;
            }
        },

        async abrirChamado(dto: AbrirChamadoAxion, arquivos: File[] = []): Promise<ChamadoAxion> {
            return this.service.abrirChamado(dto, arquivos);
        },

        async fetchTemModuloChamado(idContato: IdContato) {
            try {
                const { temModulo } = await this.service.temModuloChamado(idContato);
                this.temModuloChamadoByContato = { ...this.temModuloChamadoByContato, [idContato]: temModulo };
            } catch (e) {
                console.error('[integracaoAxionStore] fetchTemModuloChamado:', e);
                this.temModuloChamadoByContato = { ...this.temModuloChamadoByContato, [idContato]: false };
            }
        },

        async fetchChamadosDoContato(idContato: IdContato) {
            this.isFetchingChamadosDoContato = true;
            try {
                this.chamadosDoContato = await this.service.listarChamadosDoContato(idContato);
            } catch (e) {
                console.error('[integracaoAxionStore] fetchChamadosDoContato:', e);
                toast.error('Não foi possível carregar os chamados do contato.');
            } finally {
                this.isFetchingChamadosDoContato = false;
            }
        },

        async fetchOpcoesProcesso(idContato: IdContato) {
            const pedido = ++this.pedidoOpcoesProcesso;
            this.isFetchingOpcoesProcesso = true;
            this.opcoesProcesso = null;
            try {
                const opcoes = await this.service.listarOpcoesProcesso(idContato);
                if (pedido === this.pedidoOpcoesProcesso) this.opcoesProcesso = opcoes;
            } catch (e) {
                if (pedido !== this.pedidoOpcoesProcesso) return;
                console.error('[integracaoAxionStore] fetchOpcoesProcesso:', e);
                const message = (e as { message?: string })?.message;
                toast.error(message ?? 'Não foi possível carregar as opções de processo.');
            } finally {
                if (pedido === this.pedidoOpcoesProcesso) this.isFetchingOpcoesProcesso = false;
            }
        },

        async dispararProcesso(dto: DispararProcessoAxion, arquivos: File[] = []): Promise<ProcessoDisparadoAxion> {
            return this.service.dispararProcesso(dto, arquivos);
        },

        async fetchProcessosDoContato(idContato: IdContato) {
            const pedido = ++this.pedidoProcessosDoContato;
            this.isFetchingProcessosDoContato = true;
            this.processosDoContato = [];
            try {
                const processos = await this.service.listarProcessosDoContato(idContato);
                if (pedido === this.pedidoProcessosDoContato) this.processosDoContato = processos;
            } catch (e) {
                if (pedido !== this.pedidoProcessosDoContato) return;
                console.error('[integracaoAxionStore] fetchProcessosDoContato:', e);
                toast.error('Não foi possível carregar os processos do contato.');
            } finally {
                if (pedido === this.pedidoProcessosDoContato) this.isFetchingProcessosDoContato = false;
            }
        },

        async acessarProcesso(idContato: IdContato, idProcesso: string) {
            // Abre a aba antes do await: navegador bloqueia window.open fora do gesto do usuário.
            const aba = window.open('', '_blank');
            try {
                const resposta = await this.service.acessarProcesso(idContato, idProcesso);
                if (resposta?.url && aba) aba.location.href = resposta.url;
                else {
                    aba?.close();
                    toast.error('Não foi possível abrir o processo no AXION.');
                }
            } catch (e) {
                aba?.close();
                console.error('[integracaoAxionStore] acessarProcesso:', e);
                toast.error('Não foi possível abrir o processo no AXION.');
            }
        },

        async acessarOportunidade(idContato: IdContato, idOportunidade: string) {
            const { url } = await this.service.acessarOportunidade(idContato, idOportunidade);
            if (url) window.open(url, '_blank');
        },

        async acessarFicha(idContato: IdContato) {
            const { url } = await this.service.acessarFicha(idContato);
            if (url) window.open(url, '_blank');
        },

        async gerarTarefa(idContato: IdContato, idAtendimento: string) {
            const { url } = await this.service.gerarTarefa(idContato, idAtendimento);
            if (url) window.open(url, '_blank');
        },

        async desvincularResponsavel(idContato: IdContato) {
            await this.service.desvincularResponsavel(idContato);
            const chatStore = (await import('./chatStore')).useChatStore();
            const contato = chatStore.contacts.find(c => c.id === idContato);
            if (contato) {
                contato.idClienteAxion = undefined;
                contato.nomeClienteAxion = undefined;
                contato.idResponsavelAxion = undefined;
            }
            for (const chat of chatStore.chatList.filter(c => c.idContato === idContato)) {
                chat.idClienteAxion = undefined;
                chat.nomeClienteAxion = undefined;
                chat.idResponsavelAxion = undefined;
            }
            for (const chat of chatStore.chatList.filter(c => c.idContato === idContato)) {
                chat.idClienteAxion = undefined;
                chat.idResponsavelAxion = undefined;
            }
        },
    },
});
